import { useFormatter, useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BaseLineCopy } from '@/app/components/icons/BaseLineCopy';
import { BaseLineFeedback } from '@/app/components/icons/BaseLineFeedback';
import { CollectBtn } from '@/app/components/shared/Community';
import { EllipsisDropdownMenu } from '@/app/components/shared/EllipsisDropdownMenu';
import { ExtendPromptsDisplay, hasExtendDisplayPrompts } from '@/app/components/shared/ExtendPromptsDisplay';
import LikeButton from '@/app/components/shared/LikeButton';
import { MediaPromptsDisplay } from '@/app/components/shared/MediaPromptsDisplay';
import { MultiFrameTextPrompt } from '@/app/components/shared/MultiFrameTextPrompt';
import { hasTextWithTagsContent, TextWithTags } from '@/app/components/shared/TextWithTags';
import { getAudioDisplayName, UploadedAudioCard } from '@/app/components/shared/UploadedAudioCard';
import { Button } from '@/app/components/ui/button';
import {
  MODEL_VERSION,
  OPTION_LABEL_MAP,
  SCENE,
  SITE,
  TRACE_EVENT_ID,
  TRACE_EVENT_NAME,
  TYPE_KEY_MAP,
} from '@/app/constants';
import { useCopy, useDevice, useGlobalModal, useModelVersionText, useRecreate, useResolution } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { ResolutionType, SettingsLanguage } from '@/app/types';
import {
  type ActionDialogType,
  CommunityEnum,
  type DisplayModelVersion,
  EllipsisActionTypeEnum,
  type FormTabType,
} from '@/app/types';
import {
  cn,
  formatAspectRatio,
  getReportParams,
  isExtendTask,
  isImageToVideoTaskType,
  isNoRecreateTask,
  reportEvent,
} from '@/app/utils';
import { getCreationVideoAspectRatio, isHevcSupported } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';
import type { InspirationInterface } from '../types';
import { getDisplayIpLocation, getTaskInputs } from '../utils';
import { ActivityInfo } from './ActivityInfo';
import { CommunityShareBtn } from './CommunityShareBtn';
import { FeedDetailMediaFrame } from './FeedDetailMediaFrame';
import { ProTextPrompt } from './ProTextPrompt';
import { UserInfo } from './UserInfo';

const ORIGIN_RESOLUTION_ARR = ['512', '720p', '1080p'];

export const Inspiration = ({
  inspiration,
  onVideoPlayEnd,
  handleCollect,
  handleUnCollect,
  onClose,
  onRecreateStart,
  onRecreateToHome,
  handleVote,
  handleUnVote,
  mediaAreaRef,
}: InspirationInterface) => {
  const t = useTranslations();
  const { getModelVersionText } = useModelVersionText();
  const format = useFormatter();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const date = new Date(inspiration.info.created_at ?? '');
  const pathname = usePathname();
  const isActivity = inspiration.isActivity;
  const currentResolution = inspiration.info?.creation?.resolution?.resolution;
  const { open: openActionDialog } = useGlobalModal('actionDialog');
  const { copy } = useCopy();
  const { resolutionMap } = useResolution();
  const { userInfo } = useStore((state) => ({
    userInfo: state.userInfo,
  }));
  const { handleRecreate } = useRecreate();
  const { isMobile } = useDevice();
  const modelVersion = inspiration.task?.settings.model_version;

  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [isError, setIsError] = useState(false);
  const creationUri = inspiration.info.creation.uri;

  useEffect(() => {
    setAspectRatio(null);
    setIsError(false);
    const fetchVideoMetadata = async () => {
      const nextAspectRatio = await getCreationVideoAspectRatio({
        videoUrl: creationUri,
        resolution: inspiration.info.creation.resolution,
        shouldProbeMetadata: !isMobile,
      });
      setAspectRatio(nextAspectRatio ?? 16 / 9);
    };

    fetchVideoMetadata();
  }, [creationUri, inspiration.info.creation.resolution, isMobile]);

  const taskInfo = useMemo(() => getTaskInputs(inspiration.task), [inspiration.task]);
  const musicVideoAudioPrompt =
    taskInfo?.taskType === 'music_video' ? taskInfo.prompts.find((prompt) => prompt.type === 'audio') : undefined;
  const musicVideoAudioSource = musicVideoAudioPrompt?.content || '';
  const musicVideoAudioName =
    getAudioDisplayName(musicVideoAudioSource, musicVideoAudioPrompt?.name) || t('tools.oneClickMv.audioUpload.label');
  const showRecreate = useMemo(() => {
    return !['unspecified'].includes(inspiration.task?.type || '') && !isNoRecreateTask(inspiration.task?.type);
  }, [inspiration.task]);

  const handleCopyPrompt = () => {
    if (!taskInfo) return;
    copy(taskInfo.textPrompt);
  };

  const handleActionDialog = (action: EllipsisActionTypeEnum) => {
    const reportParams = inspiration.task?.type
      ? getReportParams(inspiration.task?.type, taskInfo?.settings, taskInfo?.templateScene)
      : {};
    openActionDialog({
      action: action as ActionDialogType,
      id: inspiration.info.id,
      type: 'creation',
      reportParams: reportParams,
    });
  };

  /**
   * 媒体区域
   * @returns
   */
  // 设备探测（建一个临时 video 调 canPlayType）只跟设备相关，挂载一次即可。
  const deviceSupportsHevc = useMemo(() => isHevcSupported(), []);
  const isH265NotSupported = useMemo(
    () => !deviceSupportsHevc && taskInfo?.settings?.codec?.startsWith('h265'),
    [deviceSupportsHevc, taskInfo?.settings?.codec],
  );

  const renderMediaArea = useCallback(() => {
    return (
      <FeedDetailMediaFrame
        src={creationUri}
        poster={inspiration.info.creation.cover_uri || ''}
        aspectRatio={aspectRatio}
        exploreTab={CommunityEnum.MediaAsset}
        creationId={inspiration.info.id}
        autoPlay={true}
        hasAudio={inspiration.task?.creations?.[0]?.has_audio}
        isError={isError}
        errorContent={
          isH265NotSupported
            ? t('create.char2VideoForm.video.h265NotSupported')
            : t('create.char2VideoForm.video.error')
        }
        mediaAreaRef={mediaAreaRef as React.RefObject<HTMLDivElement | null>}
        onPlayEnd={onVideoPlayEnd}
        onError={() => setIsError(true)}
      />
    );
  }, [
    aspectRatio,
    inspiration.info.creation.cover_uri,
    inspiration.info.id,
    inspiration.task?.creations,
    inspiration.task?.id,
    isError,
    isH265NotSupported,
    mediaAreaRef,
    onVideoPlayEnd,
    t,
    creationUri,
  ]);

  /**
   * 视频灵感信息栏
   * @returns
   */
  const renderInfo = () => {
    const isUpscale = inspiration.info.creation.grade === 'upscaled';

    return (
      <>
        <div className="flex h-full w-[304px] flex-1 flex-col overflow-y-auto md:w-full">
          {inspiration.info.creator ? (
            <UserInfo
              user={inspiration.info.creator}
              ipLocation={getDisplayIpLocation(inspiration.info.creator.id, inspiration.ip_location)}
            />
          ) : userInfo ? (
            <UserInfo user={userInfo} ipLocation={getDisplayIpLocation(userInfo.id, inspiration.ip_location)} />
          ) : null}

          {inspiration.info.brief && (
            <div className="mt-3 break-all text-sm text-white leading-5 md:mt-2">{inspiration.info.brief}</div>
          )}

          <div className={cn('mt-6 md:mt-5', !isUpscale && 'md:hidden')}>
            <span className="font-semibold text-sm text-system-white">{t('creations.detail.title')}</span>
          </div>

          <div
            className={cn(
              'mt-5 flex flex-col gap-3 md:mt-4 md:gap-2',
              !hasTextWithTagsContent(taskInfo?.textPrompt, taskInfo?.prompts) && 'flex-initial',
            )}
          >
            {
              // 媒体区域（图片/视频/主体）
              !isExtendTask(taskInfo?.taskType) && taskInfo?.mediaPrompts && taskInfo.mediaPrompts.length > 0 && (
                <div>
                  <MediaPromptsDisplay prompts={taskInfo.mediaPrompts} taskType={taskInfo.taskType} />
                </div>
              )
            }
            {taskInfo?.taskType === 'music_video' && musicVideoAudioSource && (
              <div className="rounded-12 border border-system-white08 bg-system-white04 backdrop-blur-[30px]">
                <UploadedAudioCard audioName={musicVideoAudioName} showPlayButton={false} showRemoveButton={false} />
              </div>
            )}
            {isExtendTask(taskInfo?.taskType) &&
              taskInfo?.prompts &&
              taskInfo.prompts.length > 0 &&
              hasExtendDisplayPrompts(taskInfo.prompts) && (
                <div className="md:mb-2">
                  <ExtendPromptsDisplay prompts={taskInfo.prompts} />
                </div>
              )}
            {taskInfo && hasTextWithTagsContent(taskInfo.textPrompt, taskInfo.prompts) && (
              <div>
                {taskInfo.isProMode && (
                  <div className="mb-2 text-system-white48 text-xs">
                    <span className="ml-2 rounded-6 bg-system-blue02/30 px-2 py-0.5 text-system-white text-xs">
                      {t('create.proMode.proText')}
                    </span>
                  </div>
                )}
                <div className="relative w-full text-sm text-system-white leading-5">
                  {taskInfo.isProMode ? (
                    <ProTextPrompt text={taskInfo.textPrompt} />
                  ) : taskInfo.taskType === 'multi_frame' ? (
                    <MultiFrameTextPrompt
                      prompts={taskInfo.prompts.filter((p) => p.type === 'text' && p.multi_frame)}
                    />
                  ) : (
                    <>
                      <TextWithTags
                        text={taskInfo.textPrompt}
                        prompts={taskInfo.prompts}
                        onClick={onClose}
                        tagClassName="bg-white/10 hover:bg-system-white/20"
                      />
                      {taskInfo?.textPrompt && (
                        <Button size="original" variant="text" className="ml-2 translate-y-0.5">
                          <BaseLineCopy
                            className="size-4 text-system-white64 hover:text-system-white"
                            onClick={handleCopyPrompt}
                          />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {renderSettings()}

            {renderExtraInfo()}

            {renderActivityInfo()}
          </div>

          {renderInspirationOperations()}
        </div>

        <div className="mt-4 w-full">{renderCommonOperations()}</div>
      </>
    );
  };

  /**
   * 获取时长显示文本
   * @param duration 时长值，-1 表示 auto
   * @returns 格式化后的时长文本
   */
  const getDurationText = useCallback(
    (duration?: number) => {
      if (!duration) return '';
      if (duration === -1) {
        return t('create.createForm.settings.duration.option.auto');
      }
      return t('create.createForm.settings.duration.option.text', { time: duration });
    },
    [t],
  );

  const modelVersionText = getModelVersionText({
    modelVersion: modelVersion as DisplayModelVersion,
    transition: taskInfo?.settings.transition,
  });

  /**
   * 基本的参数信息
   * @returns
   */
  const renderSettings = () => {
    if (!taskInfo?.settings)
      return (
        <div className="break-all pb-6 text-sm text-white leading-5">{t('create.createForm.settings.noOrigin')}</div>
      );

    const renderTag = (tag: string) => (
      <span className="h-6 rounded-6 bg-system-white10 px-2 py-1 text-system-white64 text-xs">{tag}</span>
    );
    const isUpscale = inspiration.info.creation.grade === 'upscaled';

    // 模版只展示模版名称和 aspect_ratio
    if (taskInfo.taskType === SCENE) {
      return (
        <div className="flex flex-wrap items-center gap-1">
          {/* 任务类型 */}
          {renderTag(t(`create.${TYPE_KEY_MAP.scene}`))}
          {/* 模板标签 */}
          {inspiration.info?.tag && renderTag(inspiration.info?.tag)}
          {/* 宽高比 */}
          {taskInfo.settings.aspect_ratio && renderTag(formatAspectRatio(taskInfo.settings.aspect_ratio, t))}
        </div>
      );
    }

    if (isExtendTask(taskInfo.taskType)) {
      return (
        <div className="flex flex-wrap items-center gap-1">
          {/* 任务类型 */}
          {renderTag(t(`create.${TYPE_KEY_MAP[taskInfo.taskType]}`))}
          {/* 模型版本 */}
          {modelVersionText && renderTag(modelVersionText)}
          {/* 时长 */}
          {renderTag(getDurationText(inspiration.info.creation.duration))}
          {/* 清晰度 */}
          {isUpscale
            ? renderTag(
                `SR: ${resolutionMap[inspiration.info.creation.source_creation?.resolution as keyof typeof resolutionMap]} → ${resolutionMap[currentResolution as keyof typeof resolutionMap]}`,
              )
            : // 清晰度
              taskInfo.settings.resolution &&
              ORIGIN_RESOLUTION_ARR.includes(taskInfo.settings.resolution) &&
              renderTag(
                t(`create.createForm.settings.resolution.option${taskInfo.settings.resolution as ResolutionType}.text`),
              )}
          {/* 编码格式 */}
          {taskInfo.settings.codec &&
            taskInfo.settings.codec !== 'unspecified' &&
            renderTag(
              t(
                `create.createForm.settings.${OPTION_LABEL_MAP[taskInfo.settings.codec as keyof typeof OPTION_LABEL_MAP]}.text`,
              ),
            )}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap items-center gap-1">
        {/* 任务类型 */}
        {renderTag(t(`create.${TYPE_KEY_MAP[taskInfo.taskType]}`))}
        {/* 模型版本 */}
        {modelVersionText && renderTag(modelVersionText)}
        {/* 风格 */}
        {taskInfo.settings.style &&
          taskInfo.taskType === 'text2video' &&
          renderTag(
            t(
              `create.createForm.settings.${OPTION_LABEL_MAP[taskInfo?.settings.style as keyof typeof OPTION_LABEL_MAP]}.text`,
            ),
          )}
        {/* 兼容 1.0 模型，如果 model 为 vidu 或者其他值，则不展示 */}
        {/* 1.0 模型 清晰度 */}
        {modelVersion === '1.0' &&
          taskInfo.settings.model &&
          ['vidu-high-quality', 'vidu-high-performance'].includes(taskInfo.settings.model) &&
          renderTag(
            t(
              `create.createForm.settings.${OPTION_LABEL_MAP[taskInfo.settings.model as keyof typeof OPTION_LABEL_MAP]}.text`,
            ),
          )}
        {/* 非 1.0 模型 */}
        {modelVersion !== '1.0' && (
          <>
            {/* 原始清晰度 => 当前清晰度 */}
            {isUpscale
              ? renderTag(
                  `SR: ${resolutionMap[inspiration.info.creation.source_creation?.resolution as keyof typeof resolutionMap]} → ${resolutionMap[currentResolution as keyof typeof resolutionMap]}`,
                )
              : // 清晰度
                taskInfo.settings.resolution &&
                ORIGIN_RESOLUTION_ARR.includes(taskInfo.settings.resolution) &&
                renderTag(
                  t(
                    `create.createForm.settings.resolution.option${taskInfo.settings.resolution as ResolutionType}.text`,
                  ),
                )}
            {/* 风格偏好 */}
            {taskInfo.settings.transition &&
              taskInfo.settings.transition !== 'unspecified' &&
              modelVersion === MODEL_VERSION.Q1 &&
              isImageToVideoTaskType(taskInfo.taskType) &&
              renderTag(
                t(
                  `create.createForm.settings.${OPTION_LABEL_MAP[taskInfo.settings.transition as keyof typeof OPTION_LABEL_MAP]}.text`,
                ),
              )}
            {/* 生成模式 */}
            {taskInfo.settings.transition &&
              taskInfo.settings.transition !== 'unspecified' &&
              modelVersion === MODEL_VERSION.Q2 &&
              isImageToVideoTaskType(taskInfo.taskType) &&
              renderTag(
                t(
                  `create.createForm.settings.${OPTION_LABEL_MAP[taskInfo.settings.transition as keyof typeof OPTION_LABEL_MAP]}.text`,
                ),
              )}
            {/* 宽高比 */}
            {!isImageToVideoTaskType(taskInfo.taskType) &&
              (() => {
                const formattedRatio = formatAspectRatio(taskInfo.settings.aspect_ratio, t, {
                  taskType: taskInfo.taskType as Exclude<FormTabType, 'scene'>,
                  modelVersion: taskInfo.settings.model_version as DisplayModelVersion,
                });
                return formattedRatio ? renderTag(formattedRatio) : null;
              })()}
            {/* 编码格式 */}
            {taskInfo.settings.codec &&
              taskInfo.settings.codec !== 'unspecified' &&
              renderTag(
                t(
                  `create.createForm.settings.${OPTION_LABEL_MAP[taskInfo.settings.codec as keyof typeof OPTION_LABEL_MAP]}.text`,
                ),
              )}
            {taskInfo.taskType === 'one_click' &&
              taskInfo.settings?.biz_scene &&
              renderTag(
                t(
                  `tools.oneClickVideoProduction.scenario.${taskInfo.settings.biz_scene as 'general' | 'ecommerce_ad'}.label`,
                ),
              )}
            {taskInfo.taskType === 'one_click' &&
              taskInfo.settings?.biz_scene === 'ecommerce_ad' &&
              taskInfo.settings?.voice_lang &&
              renderTag(t(`tools.languageOptions.${taskInfo.settings.voice_lang as SettingsLanguage}`))}
            {taskInfo.taskType === 'remake' &&
              taskInfo.settings?.retain_original_audio &&
              renderTag(t('tools.videoRemake.settings.withAudio'))}
          </>
        )}
      </div>
    );
  };

  /**
   * 视频灵感的额外信息
   * 包括：灵感被使用次数、创建时间、AI 生成水印
   * 不能做同款的灵感不显示灵感被使用次数
   * @returns
   */
  const renderExtraInfo = () => {
    return (
      <div className="flex flex-wrap items-center md:mt-1 [&>span.hidden+span]:before:content-none [&>span:not(:first-child)]:before:mx-2 [&>span:not(:first-child)]:before:inline-block [&>span:not(:first-child)]:before:h-[10px] [&>span:not(:first-child)]:before:w-[1px] [&>span:not(:first-child)]:before:bg-system-white/20 [&>span:not(:first-child)]:before:content-['']">
        <span className={cn('text-system-white/50 text-xs', !showRecreate && 'hidden')}>
          {t('creations.detail.inspired', { count: inspiration.info.recreate_count || 0 })}
        </span>
        {inspiration.info.created_at && (
          <span className="text-system-white/50 text-xs">
            {format.dateTime(date, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              timeZone,
            })}
          </span>
        )}
        {SITE === 'china' && <span className="text-system-white/50 text-xs">内容由 AI 生成</span>}
      </div>
    );
  };

  /**
   * 活动投稿的信息
   * 包括：活动名称、活动状态、奖项
   */
  const renderActivityInfo = () => {
    if (!inspiration.activity) return null;
    return <ActivityInfo activity={inspiration.activity} award={inspiration.info.award} />;
  };

  /**
   * 针对视频灵感的操作按钮
   * 包括：做同款
   * @returns
   */
  const renderInspirationOperations = () => {
    const { type: taskType = 'unspecified', id: taskId = '', input: taskInput } = inspiration.task || {};
    if (!showRecreate) return null;

    return (
      <div className="mt-6 flex w-full justify-center gap-2 md:mt-5 md:gap-3">
        <Button
          className="h-10 w-full"
          variant="primary"
          onClick={() => {
            if (!taskInput) {
              return;
            }
            const recreateParams = {
              input: taskInput,
              creationId: inspiration.info.creation.id || '',
              inspirationId: inspiration.info.id,
              openNewTab: true,
              settings: taskInfo?.settings || {},
              source: 'inspiration' as const,
              taskId,
              taskType,
              templateScene: taskInfo?.templateScene,
            };
            if (
              !onRecreateToHome?.({
                taskId,
                creationId: recreateParams.creationId,
                taskType,
                settings: recreateParams.settings,
                input: taskInput,
                inspirationId: inspiration.info.id,
                source: 'inspiration',
              })
            ) {
              onRecreateStart?.();
              handleRecreate(recreateParams);
            } else {
              onRecreateStart?.();
            }
            reportEvent(TRACE_EVENT_NAME[CommunityEnum.MediaAsset], {
              [TRACE_EVENT_ID[CommunityEnum.MediaAsset]]: inspiration.info.id,
              interaction_position: pathname,
              interaction_type: 'recreate',
              trace_id: inspiration.traceId || '',
              content_type: 'video',
            });
          }}
        >
          {t('home.explore.promptButton')}
        </Button>
      </div>
    );
  };

  /**
   * 在信息栏最底部展示的通用操作按钮
   * 包括：收藏、分享、更多、点赞
   * @returns
   */
  const renderCommonOperations = () => {
    const { react_info } = inspiration.info;

    return (
      <div className="flex h-10 items-center gap-2 md:h-12 md:gap-3">
        <CollectBtn
          key={inspiration.info.id}
          id={inspiration.info.id}
          tabType={CommunityEnum.MediaAsset}
          itemCollect={inspiration.info.react_info?.collected ?? false}
          itemCollectCount={inspiration.info.react_info?.collect_count ?? 0}
          handleCollect={handleCollect}
          handleUnCollect={handleUnCollect}
          hideCount={true}
          className={cn(
            'flex aspect-square size-10 h-full cursor-pointer items-center justify-center rounded-10 bg-white/10 text-lg backdrop-blur-lg hover:bg-system-white/20 md:size-12 md:rounded-12',
            isActivity && 'hidden',
          )}
          iconClassName="size-5 md:size-6"
          traceId={inspiration.traceId}
        />
        <CommunityShareBtn id={inspiration.info.id} type={CommunityEnum.MediaAsset} isActivity={isActivity} />
        {SITE === 'china' && (
          <EllipsisDropdownMenu
            className="aspect-square size-10 h-full rounded-10 bg-white/10 text-lg backdrop-blur-lg hover:bg-system-white/20 md:size-12 md:rounded-12 md:text-2xl"
            actions={[{ action: EllipsisActionTypeEnum.InspirationReport, icon: <BaseLineFeedback /> }]}
            onSelectedAction={handleActionDialog}
          />
        )}
        {react_info && (
          <LikeButton
            className="h-full flex-1 rounded-10 bg-white/10 text-lg backdrop-blur-lg hover:bg-system-white/20 md:size-12 md:rounded-12"
            id={inspiration.info.id}
            itemVote={react_info?.voted || false}
            tabType={CommunityEnum.MediaAsset}
            itemVoteCount={react_info?.vote_count || 0}
            handleVote={handleVote}
            handleUnVote={handleUnVote}
            traceId={inspiration.traceId}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <div
        className="absolute inset-0 z-[-1] bg-black/60 bg-center bg-cover bg-no-repeat blur-[150px] md:hidden"
        style={{
          backgroundImage: `url('${inspiration.info.creation.cover_uri}')`,
        }}
      >
        <div className="absolute inset-0 z-[-1] size-full bg-black/60" />
      </div>

      <div className="relative flex size-full flex-1 md:flex-col md:overflow-y-auto md:overflow-x-hidden">
        {renderMediaArea()}

        {/* 右侧信息栏 */}
        <div className="relative my-8 flex flex-col border-system-white10 border-l pr-[30px] pl-5 md:my-0 md:flex-1 md:border-0 md:px-4 md:pb-6">
          {renderInfo()}
        </div>
      </div>
    </>
  );
};
